import { useDispatch } from 'react-redux'
import 'whatwg-fetch'
import { stringify } from 'query-string'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, ResponseType } from 'axios'
import { useRelogin } from '../use-relogin'
import { authFetchAdd, authFetchSub } from '../../State/Auth/AuthActions'
import { useTypedSelector } from '../../State/RootReducer'
import { FetchErrorCodes, StatusCodes } from '../../Libs'
import { AuthFetchError } from '../../Types/api'
import JWT from 'jsonwebtoken'
import { Decoded } from '../../Types/globalTypes'
import { normalizedRequestUrl, stringifyError, useNewRelic } from '../../Libs/observability-tool'
import { useContext } from 'react'
import { AuthContext } from '../../Libs/authentication'

export interface Payload {
  filter?: string
  accountLinkFilter?: string
  filterOr?: string
  connect?: string
  sort?: string
  limit?: number
  offset?: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  decoded?: string | Record<string, any> | boolean
  reAuthToken?: string | boolean
  searchPhrase?: string
}

export type RequestType = AxiosRequestConfig['method']

interface RequestHeaders {
  'Accept-Language': string
}

export interface FetchProps {
  path: string
  method?: AxiosRequestConfig['method']
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: Payload | FormData | any
  isRetry?: boolean
  type?: ResponseType
  token?: string
  endpoint?: string
  option?: AxiosRequestConfig
  abortSignal?: AbortSignal
  headers?: RequestHeaders
}
export interface FetchPropsWithResponse extends FetchProps {
  returnResponse: true
}

export function useAuthFetchWithResult(): <T>(props: FetchProps) => Promise<T> {
  return useAuthFetch(false)
}

export function useAuthFetchWithResponse(): <T>(props: FetchProps) => Promise<AxiosResponse<T>> {
  return useAuthFetch(true)
}

/**
 * AuthFetch CustomHook
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useAuthFetch(returnResponse: boolean): any {
  const { authToken } = useContext(AuthContext)
  const relogin = useRelogin()
  const dispatch = useDispatch()
  const isLogoutActive = useTypedSelector(state => state.app.isLogoutActive)
  const { pageActions } = useNewRelic()

  /**
   * fetch that appends token to header and does automatic relogin if token is expired
   */
  async function _fetch<T>(props: FetchProps): Promise<T>
  async function _fetch<T>(props: FetchPropsWithResponse): Promise<AxiosResponse<T>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function _fetch<T>(props: FetchProps): Promise<any> {
    const {
      path,
      method = 'GET',
      payload = undefined,
      isRetry = false,
      type = undefined,
      endpoint = undefined,
      token = undefined,
      option = undefined,
      abortSignal,
      headers,
    } = props

    const normalizedUrl = normalizedRequestUrl(path.startsWith('/') ? path : `/${path}`)

    try {
      // increase fetch counter
      if (!isRetry) dispatch(authFetchAdd()) // in case isRetry is set, this is just a retry after re-auth... dont change the fetch counter

      let url = (endpoint ?? process.env.REACT_APP_API_ENDPOINT) + '/' + path
      const capitalizedMethod = method.toUpperCase()

      // append GET query string
      const query = capitalizedMethod === 'GET' && payload ? stringify(payload) : ''
      if (query) url += '?' + query

      let contentType

      // append payload
      if (capitalizedMethod !== 'GET' && payload) {
        if (payload instanceof FormData) {
          contentType = 'multipart/form-data'
        } else {
          contentType = 'application/json'
        }
      }

      let tokenToUse = token ?? authToken

      if (tokenToUse) {
        const decodedToken = JWT.decode(tokenToUse) as Decoded

        if (decodedToken?.exp) {
          const currentTime = Math.floor(Date.now() / 1000)
          const timeLeft = decodedToken.exp - currentTime

          if (timeLeft < 60) {
            try {
              tokenToUse = await relogin()
            } catch (err) {
              pageActions.logEvent({ logValue: 'Relogin-Error on authFetch: ' + stringifyError(err as Error) })
            }
          }
        }
      }

      const options: AxiosRequestConfig = {
        ...option,
        method,
        data: contentType === 'application/json' ? JSON.stringify(payload) : payload,
        headers: {
          authorization: 'Bearer ' + tokenToUse,
          'content-type': contentType,
          ...(headers ?? {}),
        },
        responseType: type,
      }

      // await new Promise(r => setTimeout(r, 2000)) // testing
      let response
      try {
        response = await axios(url, { ...options, signal: abortSignal })
      } catch (err) {
        const error = err as AuthFetchError
        if (axios.isCancel(err)) {
          throw err
        }

        if (error?.response?.status === StatusCodes.UNAUTHORIZED && error?.response?.data) {
          /** Check if the response is a Blob and parse it before relogin. */
          const isJsonBlob = error.response.data instanceof Blob
          const typedBlob = error.response.data as unknown as Blob
          const result = isJsonBlob ? JSON.parse(await typedBlob.text()) : error.response.data
          const errno = result.attributes?.errno

          pageActions.logEvent({ logValue: JSON.stringify(result) })

          if (errno === FetchErrorCodes.JwtExpired || errno === FetchErrorCodes.NdaUnlockedRenewJwt) {
            if (!isRetry && !isLogoutActive) {
              const newToken = await relogin()

              if (newToken) {
                const fetchProps = { path, method, endpoint, payload, isRetry: true, type: isJsonBlob ? 'blob' : type }

                if (returnResponse) {
                  return await _fetch<T>({ ...fetchProps, returnResponse })
                }

                return await _fetch<T>(fetchProps)
              }
            }
          }
        }

        throw err
      }
      // await new Promise(r => setTimeout(r, 2000)) // testing

      pageActions.apiRequest({ normalizedUrl, httpStatus: response.status })
      dispatch(authFetchSub())

      if (returnResponse) return response
      return await response.data
    } catch (err) {
      // subtract fetch counter
      if (!isRetry) {
        if (err instanceof AxiosError && err.name !== 'CanceledError') {
          /** Don't log canceled errors to clean up the console. */
          console.error('authFetch > catch', err)
          pageActions.apiRequest({ normalizedUrl, httpStatus: err.response?.status ?? 0 })
        }
        dispatch(authFetchSub()) // in case overwriteToken is set, this is just a retry after re-auth... dont handle errors.. just throw them and let the original fetch handle it
      }
      throw err
    }
  }

  return _fetch
}
